// Saves a file the browser fetches as a Blob.
//
// On Chromium, uses the File System Access API (showSaveFilePicker): a
// user-chosen file save rather than an "automatic download", so it sidesteps
// Chrome's limit of one script-initiated download per page load (the source of
// the "download multiple files" block). Other browsers fall back to the classic
// Blob + anchor download; they don't enforce that limit.
//
// MUST be called synchronously from the click handler (before any other await)
// so showSaveFilePicker keeps the user gesture.
//
// fetchBlob: () => Promise<Blob | undefined> — invoked only after the save
// location is chosen. Return a falsy value (or throw) to abort the save, e.g.
// when the fetch failed and the error was already surfaced.
// Returns true if the file was saved, false if it was cancelled/aborted.
export const saveFile = async (fileName, fetchBlob) => {
  if ("showSaveFilePicker" in window) {
    let handle;
    try {
      handle = await window.showSaveFilePicker({ suggestedName: fileName });
    } catch (error) {
      if (error && error.name === "AbortError") return false; // user cancelled
      throw error;
    }
    const blob = await fetchBlob();
    if (!blob) return false;
    const writable = await handle.createWritable();
    try {
      await writable.write(blob);
      await writable.close();
    } catch (error) {
      await writable.abort().catch(() => {}); // release the file lock on failure
      throw error;
    }
    return true;
  }

  // Fallback (Firefox/Safari): classic blob download — no picker, and these
  // browsers don't limit script-initiated downloads per page load.
  const blob = await fetchBlob();
  if (!blob) return false;
  const fileURL = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.setAttribute("style", "display:none;");
  a.download = fileName;
  a.href = fileURL;
  try {
    document.body.appendChild(a); // required for Firefox to dispatch the click
    a.click();
  } finally {
    a.remove();
    window.URL.revokeObjectURL(fileURL); // always freed, even if the click threw
  }
  return true;
};
